import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, Card, CardBody, CardHeader, Input } from "reactstrap";

import {
  useFetchCustomerCitiesQuery,
  useFetchCustomerSummaryQuery,
  useFetchCustomersQuery,
} from "../api/api";
import { useTranslation } from "../hooks/useTranslation";
import {
  CUSTOMER_ACTIVITY_OPTIONS,
  CUSTOMER_SORT_OPTIONS,
  CUSTOMERS_PAGE_SIZE,
  avatarToneFor,
  buildCustomersListParams,
  customerInitials,
  formatCustomerDate,
  hasActiveCustomerFilters,
  readCustomersListState,
  showingRange,
  writeCustomersListState,
} from "./customerPresentation";
import { useDebounce } from "./salesUtils";

const fill = (template, values) =>
  Object.entries(values).reduce(
    (text, [key, value]) => text.replace(`{${key}}`, String(value)),
    String(template),
  );

// Every card counts CUSTOMERS (not orders); the hint states the definition.
const SUMMARY_CARDS = [
  {
    key: "totalCustomers",
    icon: "icon-single-02",
    tone: "neutral",
    labelKey: "customers.summaryTotal",
    hintKey: "customers.summaryTotalHint",
  },
  {
    key: "customersWithActiveOrders",
    icon: "icon-delivery-fast",
    tone: "info",
    labelKey: "customers.summaryActive",
    hintKey: "customers.summaryActiveHint",
  },
  {
    key: "customersWithDeliveredOrders",
    icon: "icon-check-2",
    tone: "ok",
    labelKey: "customers.summaryDelivered",
    hintKey: "customers.summaryDeliveredHint",
  },
  {
    key: "customersWithReturnedToSender",
    icon: "icon-refresh-01",
    tone: "warn",
    labelKey: "customers.summaryReturned",
    hintKey: "customers.summaryReturnedHint",
  },
];

const CountPill = ({ value, tone }) => (
  <span className={`cu-pill cu-pill--${value > 0 ? tone : "zero"}`}>{value}</span>
);

/**
 * Klientët - find and understand customers. Search, filters, sorting and
 * pagination all run on the server; the URL keeps the list state so refresh and
 * "back" land on the same view.
 */
const Customers = () => {
  const t = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [listState, setListState] = useState(() => readCustomersListState(searchParams));
  const [searchInput, setSearchInput] = useState(listState.search);
  const debouncedSearch = useDebounce(searchInput, 350).trim();

  useEffect(() => {
    setListState((current) =>
      current.search === debouncedSearch ? current : { ...current, search: debouncedSearch, page: 1 },
    );
  }, [debouncedSearch]);

  useEffect(() => {
    const next = writeCustomersListState(listState);
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [listState, searchParams, setSearchParams]);

  const {
    data: listData,
    isFetching,
    error: listError,
  } = useFetchCustomersQuery(buildCustomersListParams(listState), { refetchOnMountOrArgChange: true });
  const { data: summary } = useFetchCustomerSummaryQuery(undefined, { refetchOnMountOrArgChange: true });
  const { data: cities = [] } = useFetchCustomerCitiesQuery(undefined, { refetchOnMountOrArgChange: true });

  useEffect(() => {
    if (listError) {
      // eslint-disable-next-line no-console
      console.warn("[customers:list] API error", listError?.status, listError?.data || listError);
    }
  }, [listError]);

  const items = listData?.items || [];
  const total = Number(listData?.total || 0);
  const totalPages = Math.max(1, Number(listData?.totalPages || 1));
  const range = showingRange({ page: listState.page, pageSize: CUSTOMERS_PAGE_SIZE, total });

  const updateFilter = (field, value) =>
    setListState((current) => ({ ...current, [field]: value, page: 1 }));

  const openCustomer = (customerId) =>
    navigate(`/admin/customers/${customerId}`, { state: { listQuery: searchParams.toString() } });

  const renderRows = () => {
    if (isFetching && !listData) {
      return Array.from({ length: 5 }).map((_, index) => (
        <div className="cu-row cu-row--loading" key={`skeleton-${index}`} aria-hidden="true">
          <div className="cu-cell cu-cell--customer">
            <span className="cu-avatar cu-avatar--skeleton" />
            <span className="cu-skeleton" style={{ width: "60%" }} />
          </div>
          <span className="cu-skeleton" />
        </div>
      ));
    }

    if (listError) {
      // A failed request must never look like "no customers".
      return (
        <div className="cu-empty">
          <div className="sp-empty-title">{t("customers.loadError")}</div>
          <p className="sp-empty-desc">{t("customers.loadErrorHint")}</p>
        </div>
      );
    }

    if (!items.length) {
      const filtered = hasActiveCustomerFilters(listState);
      return (
        <div className="cu-empty">
          <div className="sp-empty-title">
            {filtered ? t("customers.noResults") : t("customers.noCustomers")}
          </div>
          <p className="sp-empty-desc">
            {filtered ? t("customers.noResultsHint") : t("customers.noCustomersHint")}
          </p>
        </div>
      );
    }

    return items.map((customer) => {
      const { stats } = customer;

      return (
        <div
          key={customer.id}
          role="button"
          tabIndex={0}
          className="cu-row"
          onClick={() => openCustomer(customer.id)}
          onKeyDown={(event) => {
            if (event.key === "Enter") openCustomer(customer.id);
          }}
        >
          <div className="cu-cell cu-cell--customer">
            <span className={`cu-avatar cu-avatar--${avatarToneFor(customer.name)}`} aria-hidden="true">
              {customerInitials(customer.name)}
            </span>
            <span className="cu-customer-text">
              <span className="cu-customer-name">{customer.name}</span>
              <span className="cu-customer-phone">{customer.phone}</span>
            </span>
          </div>
          <div className="cu-cell cu-cell--city" data-label={t("customers.colCity")}>
            {customer.city || "—"}
          </div>
          <div className="cu-cell cu-cell--num" data-label={t("customers.colTotal")}>
            {stats.totalOrders}
          </div>
          <div className="cu-cell cu-cell--pill" data-label={t("customers.colDelivered")}>
            <CountPill value={stats.deliveredOrders} tone="ok" />
          </div>
          <div className="cu-cell cu-cell--pill" data-label={t("customers.colCancelled")}>
            <CountPill value={stats.cancelledOrders} tone="danger" />
          </div>
          <div className="cu-cell cu-cell--pill" data-label={t("customers.colReturned")}>
            <CountPill value={stats.returnedToSenderOrders} tone="warn" />
          </div>
          <div className="cu-cell cu-cell--date" data-label={t("customers.colLastOrder")}>
            {formatCustomerDate(stats.lastOrderAt) || "—"}
          </div>
          <div className="cu-cell cu-cell--action">
            <Button
              size="sm"
              color="success"
              outline
              className="cu-view-btn"
              onClick={(event) => {
                event.stopPropagation();
                openCustomer(customer.id);
              }}
            >
              <i className="tim-icons icon-zoom-split" aria-hidden="true" /> {t("customers.view")}
            </Button>
          </div>
        </div>
      );
    });
  };

  return (
    <div className="content sales-premium customers-page">
      <Card className="sales-page-card">
        <CardHeader className="sales-page-header cu-header">
          <div>
            <div className="sp-title">{t("customers.title")}</div>
            <p className="sp-subtitle">{t("customers.subtitle")}</p>
          </div>
        </CardHeader>

        <CardBody className="sales-page-body">
          <section className="cu-summary" aria-label={t("customers.title")}>
            {SUMMARY_CARDS.map((card) => (
              <div className="cu-summary-card" key={card.key}>
                <span className={`cu-summary-icon cu-summary-icon--${card.tone}`} aria-hidden="true">
                  <i className={`tim-icons ${card.icon}`} />
                </span>
                <span>
                  <span className="cu-summary-label">{t(card.labelKey)}</span>
                  <span className="cu-summary-value">
                    {summary ? Number(summary[card.key] || 0).toLocaleString("sq-AL") : "—"}
                  </span>
                  <span className="cu-summary-hint">{t(card.hintKey)}</span>
                </span>
              </div>
            ))}
          </section>

          <section className="cu-filters">
            <div className="cu-search">
              <i className="tim-icons icon-zoom-split" aria-hidden="true" />
              <Input
                type="search"
                autoComplete="off"
                aria-label={t("customers.searchPlaceholder")}
                placeholder={t("customers.searchPlaceholder")}
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
              />
            </div>
            <Input
              type="select"
              aria-label={t("customers.allCities")}
              value={listState.city}
              onChange={(event) => updateFilter("city", event.target.value)}
            >
              <option value="">{t("customers.allCities")}</option>
              {cities.map((city) => (
                <option key={city} value={city}>
                  {city}
                </option>
              ))}
            </Input>
            <Input
              type="select"
              aria-label={t("customers.activityAll")}
              value={listState.activity}
              onChange={(event) => updateFilter("activity", event.target.value)}
            >
              {CUSTOMER_ACTIVITY_OPTIONS.map((option) => (
                <option key={option.value || "all"} value={option.value}>
                  {t(option.labelKey)}
                </option>
              ))}
            </Input>
            <Input
              type="select"
              aria-label={t("customers.sortNewest")}
              value={listState.sort}
              onChange={(event) => updateFilter("sort", event.target.value)}
            >
              {CUSTOMER_SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {t(option.labelKey)}
                </option>
              ))}
            </Input>
          </section>

          <div className="cu-table">
            <div className="cu-row cu-row--header" aria-hidden="true">
              <span>{t("customers.colCustomer")}</span>
              <span>{t("customers.colCity")}</span>
              <span className="cu-cell--num">{t("customers.colTotal")}</span>
              <span className="cu-cell--pill">{t("customers.colDelivered")}</span>
              <span className="cu-cell--pill">{t("customers.colCancelled")}</span>
              <span className="cu-cell--pill">{t("customers.colReturned")}</span>
              <span>{t("customers.colLastOrder")}</span>
              <span className="cu-cell--action">{t("customers.colActions")}</span>
            </div>
            {renderRows()}
          </div>

          {total > 0 && !listError ? (
            <div className="cu-pagination">
              <span className="sp-text-sm sp-text-muted">
                {fill(t("customers.showingRange"), { from: range.from, to: range.to, total })}
              </span>
              <div className="cu-pagination-controls">
                <Button
                  size="sm"
                  color="secondary"
                  outline
                  disabled={listState.page <= 1 || isFetching}
                  onClick={() => setListState((current) => ({ ...current, page: current.page - 1 }))}
                >
                  ‹ {t("customers.previous")}
                </Button>
                <span className="cu-page-indicator">
                  {listState.page} / {totalPages}
                </span>
                <Button
                  size="sm"
                  color="secondary"
                  outline
                  disabled={listState.page >= totalPages || isFetching}
                  onClick={() => setListState((current) => ({ ...current, page: current.page + 1 }))}
                >
                  {t("customers.next")} ›
                </Button>
              </div>
            </div>
          ) : null}
        </CardBody>
      </Card>
    </div>
  );
};

export default Customers;
